<template>
    <validation-observer ref="guest_verification_modal" v-slot="{ meta, errors, setFieldValue }">
        <modal
            :id="modalId"
            title="Guest Verification Request"
            scrollable
            @show="fetchData"
            :ok-disabled="!meta.valid || busy"
            @ok.prevent="sendRequest"
        >
            <validation-provider name="to_email" :rules="allGuests ? '' : 'email|required'">
                <form-input
                    floatingLabel="To:"
                    v-model="to_email"
                    :error="errors.to_email"
                    :disabled="allGuests"
                    @input="(val) => setFieldValue('to_email', val)"
                />
            </validation-provider>
            <validation-provider name="subject" rules="required">
                <form-input
                    floatingLabel="Subject:"
                    v-model="subject"
                    :error="errors.subject"
                    @input="(val) => setFieldValue('subject', val)"
                />
            </validation-provider>
            <div class="d-flex align-items-start w-100 flex-wrap flex-md-nowrap">
                <form-select
                    class="flex-grow-1"
                    :options="emailsOptions"
                    v-model="selectedEmailOption"
                    floatingLabel="Email Response"
                />
                <b-button
                    class="ml-md-2 mb-3 mb-md-0 flex-grow-1 pt-2 d-flex align-items-center justify-content-center"
                    style="padding-bottom: 0.65rem"
                    variant="primary"
                    @click="applyResponse"
                    >Apply</b-button
                >
            </div>
            <text-editor v-model="message" :options="editorModules" />
        </modal>
    </validation-observer>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import API from '../../api';
import { GUEST_VERIFICATIONS_ENDPOINT, APP_EMAIL_MOCKUP_RESPONSES } from '../../endpoints';
import { formatResponseError } from '../../utils/api';
import FormInput from '../generic/form-input/form-input.vue';
import FormSelect from '../generic/form-select/form-select.vue';
import FormTextarea from '../generic/form-textarea/form-textarea.vue';
import modal from '../generic/modal/modal.vue';

const TextEditor = defineAsyncComponent(() =>
    import(
        /* webpackChunkName: "lx-text-editor" */
        '../generic/editor/quill-editor.vue'
    )
);

export default {
    name: 'GuestVerificationRequestModal',
    components: { modal, FormInput, FormSelect, FormTextarea, TextEditor },
    props: {
        initialValues: Object,
        modalId: String,
        onUpdate: {
            type: Function,
            default: () => {},
        },
        allGuests: Boolean,
    },
    data() {
        return {
            busy: false,
            editorModules: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ header: 1 }, { header: 2 }],
                            ['clean'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                        ],
                    },
                },
            },
            emailsOptions: [],
            message: null,
            reservation: {},
            subject: 'Guest verification required',
            selectedEmailOption: null,
            to_email: null,
        };
    },
    created() {
        this.reservation = this.initialValues;
    },
    methods: {
        async applyResponse() {
            try {
                const res = await API.get(APP_EMAIL_MOCKUP_RESPONSES(this.selectedEmailOption, 0));
                this.message = res.data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async fetchData() {
            try {
                const emailResponse = await API.get(`${GUEST_VERIFICATIONS_ENDPOINT()}verification-responses/`);
                this.emailsOptions = emailResponse.data.map((item) => ({
                    value: item.id,
                    text: item.title,
                }));
                const defaultItem = emailResponse.data.find((item) => item.is_default_guest_verification_request_email);
                this.selectedEmailOption = defaultItem ? defaultItem.id : '';
                this.to_email = this.allGuests ? 'All Guests' : this.reservation.user.email;

                await this.applyResponse();
                this.$nextTick(() => {
                    this.$refs.guest_verification_modal.setValues({
                        to_email: this.to_email,
                        subject: this.subject,
                        message: this.message,
                    });
                });
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async sendRequest() {
            try {
                this.busy = true;
                await API.post(
                    `${GUEST_VERIFICATIONS_ENDPOINT()}${
                        this.allGuests ? 'send-verification-request-all' : 'send-verification-request'
                    }/`,
                    {
                        to_email: this.to_email,
                        message: this.message,
                        subject: this.subject,
                        reservation_id: this.reservation.id,
                    }
                );
                this.$bvModal.hide(this.modalId);
                this.$emit('submit');
                this.successToast({
                    text: 'Verification successfully requested',
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not send request' });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
